// frontend/src/components/ApplicationForm.js
import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../api';
import logoutManager from '../logout'; // Import the logout manager
import { Upload, FileText, AlertCircle, CheckCircle, XCircle, LogOut, Building2, Globe } from 'lucide-react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { parsePhoneNumberFromString } from 'libphonenumber-js';

const ApplicationForm = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { userId: stateUserId } = location.state || {};
  const userId = stateUserId || localStorage.getItem('userId');
  
  const [formData, setFormData] = useState({
    organizationName: '',
    subdomain: '',
    address: '',
    phone: '',
    email: '',
  });
  
  const [files, setFiles] = useState({
    addressProof: null,
    institutionProof: null
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [subdomainStatus, setSubdomainStatus] = useState({
    loading: false,
    message: '',
    valid: false
  });
  const [submitStatus, setSubmitStatus] = useState({
    success: false,
    message: ''
  });
  const [subdomainInput, setSubdomainInput] = useState('');

  // Check for userId - Only run once on mount
  useEffect(() => {
    if (!userId) {
      navigate('/signin', { state: { from: location.pathname } });
    }
  }, [userId, navigate, location.pathname]);

  // Logout handler using logout manager
  const handleLogout = async () => {
    try {
      await logoutManager.logout({
        redirectTo: '/signin',
        showToast: true,
        onSuccess: () => {
          console.log('Logout successful');
        },
        onError: (error) => {
          console.error('Logout error:', error);
        }
      });
    } catch (error) {
      console.error('Error during logout:', error);
      // Fallback logout if the manager fails
      localStorage.clear();
      sessionStorage.clear();
      window.location.href = '/signin';
    }
  };

  // validateField handles all data validation rules natively
  const validateField = useCallback((name, value) => {
    let error = '';
    
    switch (name) {
      case 'organizationName':
        if (!value.trim()) error = 'Organization name is required';
        else if (value.length < 3) error = 'Name must be at least 3 characters';
        break;
      case 'subdomain':
        if (!value) error = 'Subdomain is required';
        else if (!/^[a-z0-9-]+$/.test(value)) error = 'Only lowercase letters, numbers and hyphens allowed';
        else if (value.length < 3) error = 'Subdomain must be at least 3 characters';
        else if (value.length > 20) error = 'Subdomain must be less than 20 characters';
        break;
      case 'email':
        if (!value) error = 'Email is required';
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) error = 'Invalid email format';
        break;
      case 'phone':
        if (!value) {
          error = 'Phone number is required';
        } else {
          // Format with a leading + if it's missing for global parsing accuracy
          const fullNumber = value.startsWith('+') ? value : `+${value}`;
          const phoneNumber = parsePhoneNumberFromString(fullNumber);
          if (!phoneNumber || !phoneNumber.isValid()) {
            error = 'Invalid global phone number format';
          }
        }
        break;
      case 'address':
        if (!value.trim()) error = 'Institution address is required';
        break;
      default:
        break;
    }

    return error;
  }, []);

  // Separate function to update errors without causing loops
  const handleFieldChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    const error = validateField(name, value);
    setErrors(prev => ({ ...prev, [name]: error }));
  };

  const validateFiles = () => {
    const fileErrors = {};
    if (!files.addressProof) fileErrors.addressProof = 'Address Proof document is required';
    if (!files.institutionProof) fileErrors.institutionProof = 'Institution Proof document is required';
    return fileErrors;
  };

  const checkSubdomainAvailability = useCallback(async (subdomain) => {
    if (!subdomain) {
      setSubdomainStatus({
        loading: false,
        message: '',
        valid: false
      });
      return;
    }

    const error = validateField('subdomain', subdomain);
    if (error) {
      setSubdomainStatus({
        loading: false,
        message: '',
        valid: false
      });
      return;
    }

    setSubdomainStatus(prev => ({ ...prev, loading: true, message: '' }));

    try {
      const response = await api.get(`api/domain/check-subdomain`, {
        params: { subdomain },
      });
      
      setSubdomainStatus({
        loading: false,
        message: response.data.available 
          ? `${subdomain}.truyon.com is available` 
          : `${subdomain}.truyon.com is already taken`,
        valid: response.data.available
      });
    } catch (error) {
      console.error('Error checking subdomain:', error);
      setSubdomainStatus({
        loading: false,
        message: 'Error checking subdomain availability',
        valid: false
      });
    }
  }, [validateField]);

  // Debounced subdomain check
  useEffect(() => {
    if (!subdomainInput) return;
    
    const timer = setTimeout(() => {
      checkSubdomainAvailability(subdomainInput);
    }, 500);
    
    return () => clearTimeout(timer);
  }, [subdomainInput, checkSubdomainAvailability]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    handleFieldChange(name, value);

    if (name === 'subdomain') {
      setSubdomainInput(value);
    }
  };

  // Custom standalone handler for Phone Input changes
  const handlePhoneChange = (value) => {
    // Append leading plus symbol if not automatically included by library
    const formattedValue = value.startsWith('+') ? value : `+${value}`;
    handleFieldChange('phone', formattedValue);
  };

  const handleFileChange = (e, fileType) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      setErrors(prev => ({
        ...prev,
        [fileType]: 'File size should be less than 5MB'
      }));
      return;
    }

    const allowedTypes = [
      'image/jpeg', 'image/png', 'image/jpg', 'image/gif', 'image/webp',
      'application/pdf'
    ];
    
    if (!allowedTypes.includes(file.type)) {
      setErrors(prev => ({
        ...prev,
        [fileType]: 'Only images and PDF files are allowed'
      }));
      return;
    }

    setErrors(prev => ({ ...prev, [fileType]: null }));
    setFiles(prev => ({ ...prev, [fileType]: file }));
  };

  const removeFile = (fileType) => {
    setFiles(prev => ({ ...prev, [fileType]: null }));
    document.getElementById(fileType).value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    let hasErrors = false;
    const newErrors = {};
    
    Object.keys(formData).forEach(key => {
      const error = validateField(key, formData[key]);
      if (error) {
        newErrors[key] = error;
        hasErrors = true;
      }
    });

    const fileErrors = validateFiles();
    if (fileErrors.addressProof || fileErrors.institutionProof) {
      Object.assign(newErrors, fileErrors);
      hasErrors = true;
    }

    if (hasErrors || !subdomainStatus.valid) {
      setErrors(newErrors);
      setSubmitStatus({
        success: false,
        message: 'Please fix the errors before submitting'
      });
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus({ success: false, message: '' });

    try {
      const formPayload = new FormData();
      formPayload.append('userId', userId);
      formPayload.append('organizationName', formData.organizationName);
      formPayload.append('subdomain', formData.subdomain);
      formPayload.append('address', formData.address);
      formPayload.append('phone', formData.phone);
      formPayload.append('email', formData.email);
      
      if (files.addressProof) {
        formPayload.append('addressProof', files.addressProof);
      }
      if (files.institutionProof) {
        formPayload.append('institutionProof', files.institutionProof);
      }

      const response = await api.post('api/application', formPayload, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setSubmitStatus({
        success: true,
        message: response.data.message || 'Application submitted successfully!'
      });

      setTimeout(() => {
        navigate('/application-status', { 
          state: { userId },
          replace: true 
        });
      }, 2000);
    } catch (error) {
      console.error('Submission error:', error);
      setSubmitStatus({
        success: false,
        message: error.response?.data?.message || 
          'Failed to submit application. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getFileIcon = (file) => {
    if (!file) return <FileText className="w-5 h-5 text-slate-400" />;
    if (file.type === 'application/pdf') {
      return <FileText className="w-5 h-5 text-rose-500" />;
    }
    return <FileText className="w-5 h-5 text-blue-500" />;
  };

  if (!userId) {
    return null;
  }

  return (
    <div className="min-h-screen bg-slate-50 py-6 px-4 md:py-12 flex flex-col items-center">
      <div className="max-w-3xl w-full">
        
        {/* Header Hero */}
        <div className="text-center mb-8 md:mb-12 mt-4">
          <div className="mx-auto h-14 w-14 bg-blue-600 rounded-2xl flex items-center justify-center shadow-md shadow-blue-600/10 mb-4">
            <Building2 className="h-7 w-7 text-white" />
          </div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight">
            Institution Registration
          </h1>
          <p className="mt-2 text-sm md:text-base text-slate-500 max-w-md mx-auto leading-relaxed">
            Register your educational platform profile. Provide official verification documents below to setup workspace.
          </p>
        </div>

        {/* Content Card container */}
        <div className="bg-white shadow-sm border border-slate-200 rounded-xl overflow-hidden">
          <div className="p-5 md:p-8">
            
            {/* Top Toolbar Navigation */}
            <div className="flex justify-end border-b border-slate-100 pb-4 mb-6">
              <button
                onClick={handleLogout}
                className="inline-flex items-center gap-2 px-3.5 py-1.5 text-xs font-semibold text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <LogOut className="w-3.5 h-3.5" />
                Sign Out
              </button>
            </div>

            {/* Alert Notifications banner panel */}
            {submitStatus.message && (
              <div className={`mb-6 p-4 rounded-xl flex gap-3 ${
                submitStatus.success 
                  ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' 
                  : 'bg-rose-50 text-rose-800 border border-rose-200'
              }`}>
                {submitStatus.success ? (
                  <CheckCircle className="h-5 w-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                ) : (
                  <XCircle className="h-5 w-5 text-rose-500 flex-shrink-0 mt-0.5" />
                )}
                <p className="text-xs font-semibold leading-relaxed">{submitStatus.message}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              
              {/* Organization Data Profile Section */}
              <div className="space-y-5">
                <h2 className="text-sm font-bold text-slate-800 uppercase tracking-wider border-b border-slate-50 pb-2 mb-2">
                  Organization Profile
                </h2>
                
                <div>
                  <label htmlFor="organizationName" className="block text-xs font-semibold text-slate-700 mb-1">
                    Institution Official Name <span className="text-rose-500">*</span>
                  </label>
                  <input
                    id="organizationName"
                    name="organizationName"
                    type="text"
                    value={formData.organizationName}
                    onChange={handleChange}
                    className={`w-full text-sm px-3.5 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all ${
                      errors.organizationName ? 'border-rose-300 bg-rose-50/10 focus:border-rose-500' : 'border-slate-200 focus:border-blue-500'
                    }`}
                    placeholder="e.g. Sunrise College of Science"
                  />
                  {errors.organizationName && (
                    <p className="mt-1 text-xs text-rose-600 font-medium">{errors.organizationName}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="subdomain" className="block text-xs font-semibold text-slate-700 mb-1">
                    Request Platform Subdomain <span className="text-rose-500">*</span>
                  </label>
                  <div className="flex rounded-lg shadow-2xs relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
                      <Globe className="w-4 h-4" />
                    </div>
                    <input
                      id="subdomain"
                      name="subdomain"
                      type="text"
                      value={formData.subdomain}
                      onChange={handleChange}
                      className={`w-full text-sm pl-9 pr-28 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all ${
                        errors.subdomain ? 'border-rose-300 focus:border-rose-500' : 'border-slate-200 focus:border-blue-500'
                      }`}
                      placeholder="sunrise-college"
                    />
                    <span className="absolute inset-y-0 right-0 flex items-center px-3 rounded-r-lg border border-l border-slate-200 bg-slate-50 text-slate-500 text-xs font-medium select-none">
                      .truyon.com
                    </span>
                  </div>
                  
                  {errors.subdomain ? (
                    <p className="mt-1 text-xs text-rose-600 font-medium">{errors.subdomain}</p>
                  ) : subdomainStatus.loading ? (
                    <p className="mt-1 text-xs text-blue-600 font-medium flex items-center gap-1.5">
                      <svg className="animate-spin h-3.5 w-3.5 text-blue-500" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Verifying availability...
                    </p>
                  ) : subdomainStatus.message && (
                    <p className={`mt-1 text-xs font-medium ${subdomainStatus.valid ? 'text-emerald-600' : 'text-rose-600'}`}>
                      {subdomainStatus.message}
                    </p>
                  )}
                </div>

                <div>
                  <label htmlFor="address" className="block text-xs font-semibold text-slate-700 mb-1">
                    Complete Premises Address <span className="text-rose-500">*</span>
                  </label>
                  <input
                    id="address"
                    name="address"
                    type="text"
                    value={formData.address}
                    onChange={handleChange}
                    className={`w-full text-sm px-3.5 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all ${
                      errors.address ? 'border-rose-300 bg-rose-50/10 focus:border-rose-500' : 'border-slate-200 focus:border-blue-500'
                    }`}
                    placeholder="123 MG Road, Bangalore, Karnataka"
                  />
                  {errors.address && (
                    <p className="mt-1 text-xs text-rose-600 font-medium">{errors.address}</p>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="phone" className="block text-xs font-semibold text-slate-700 mb-1">
                      Primary Phone Number <span className="text-rose-500">*</span>
                    </label>
                    <div className="custom-phone-input-container">
                      <PhoneInput
                        country={'in'}
                        value={formData.phone}
                        onChange={handlePhoneChange}
                        placeholder="+91 98765-43210"
                        inputProps={{ id: 'phone', name: 'phone', required: true }}
                        containerClass="w-full"
                        inputClass={`!w-full !text-sm !h-[40px] !border !rounded-lg focus:!outline-none focus:!ring-2 focus:!ring-blue-500/20 !transition-all ${
                          errors.phone ? '!border-rose-300 !bg-rose-50/10 focus:!border-rose-500' : '!border-slate-200 focus:!border-blue-500'
                        }`}
                        buttonClass="!border !border-slate-200 !rounded-l-lg !bg-slate-50"
                      />
                    </div>
                    {errors.phone && (
                      <p className="mt-1 text-xs text-rose-600 font-medium">{errors.phone}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-xs font-semibold text-slate-700 mb-1">
                      Administrative Email <span className="text-rose-500">*</span>
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={`w-full text-sm px-3.5 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all ${
                        errors.email ? 'border-rose-300 bg-rose-50/10 focus:border-rose-500' : 'border-slate-200 focus:border-blue-500'
                      }`}
                      placeholder="admin@sunrisecollege.edu"
                    />
                    {errors.email && (
                      <p className="mt-1 text-xs text-rose-600 font-medium">{errors.email}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Document Assets Upload Section */}
              <div className="border-t border-slate-100 pt-6 mt-6">
                <h2 className="text-sm font-bold text-slate-800 uppercase tracking-wider mb-1">
                  Verification Records
                </h2>
                <p className="text-xs text-slate-400 mb-5 leading-relaxed">
                  Provide clear, official credentials confirming institutional legitimacy and current address details.
                </p>

                {/* Address File Input Wrapper card */}
                <div className="mb-5 p-4 bg-slate-50/60 rounded-xl border border-slate-200">
                  <h3 className="text-xs font-bold text-slate-700 mb-1 flex items-center gap-2">
                    <FileText className="w-4 h-4 text-blue-600 flex-shrink-0" />
                    1. Property Address Proof <span className="text-rose-500">*</span>
                  </h3>
                  <div className="text-[11px] text-slate-400 space-y-0.5 pl-6 mb-3">
                    <p>• Rental/Lease deeds or official ownership sale agreement records</p>
                    <p>• Recent local corporation premises tax evaluation receipts</p>
                    <p>• Active commercial electricity power connection bills</p>
                  </div>
                  
                  <div className="mt-2">
                    <input
                      type="file"
                      id="addressProof"
                      accept=".jpg,.jpeg,.png,.gif,.webp,.pdf"
                      onChange={(e) => handleFileChange(e, 'addressProof')}
                      className="hidden"
                    />
                    
                    {!files.addressProof ? (
                      <label
                        htmlFor="addressProof"
                        className="flex flex-col items-center justify-center w-full p-5 border-2 border-dashed border-slate-200 rounded-xl cursor-pointer bg-white hover:bg-slate-50/80 hover:border-blue-400 transition-all"
                      >
                        <Upload className="h-5 w-5 text-slate-400 mb-1" />
                        <span className="text-xs font-semibold text-slate-600">Select address proof file</span>
                        <span className="text-[10px] text-slate-400 mt-0.5">Images or PDF up to 5MB</span>
                      </label>
                    ) : (
                      <div className="flex items-center justify-between p-3 bg-white border border-slate-200 rounded-lg shadow-3xs">
                        <div className="flex items-center min-w-0 gap-2">
                          {getFileIcon(files.addressProof)}
                          <span className="text-xs font-semibold text-slate-700 truncate">
                            {files.addressProof.name}
                          </span>
                          <span className="text-[10px] text-slate-400 flex-shrink-0">
                            ({(files.addressProof.size / 1024 / 1024).toFixed(2)} MB)
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeFile('addressProof')}
                          className="p-1 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded transition-colors"
                        >
                          <XCircle className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                    
                    {errors.addressProof && (
                      <p className="mt-1 text-xs text-rose-600 font-medium flex items-center gap-1">
                        <AlertCircle className="w-3.5 h-3.5" />
                        {errors.addressProof}
                      </p>
                    )}
                  </div>
                </div>

                {/* Institution Establishment Input Wrapper card */}
                <div className="p-4 bg-slate-50/60 rounded-xl border border-slate-200">
                  <h3 className="text-xs font-bold text-slate-700 mb-1 flex items-center gap-2">
                    <FileText className="w-4 h-4 text-purple-600 flex-shrink-0" />
                    2. Establishment Credentials <span className="text-rose-500">*</span>
                  </h3>
                  <div className="text-[11px] text-slate-400 space-y-0.5 pl-6 mb-3">
                    <p>• Trust or Society registration certificate profiles</p>
                    <p>• Official recognition decrees from governing educational bodies (UGC, AICTE)</p>
                    <p>• Valid organization PAN framework or GST certificates</p>
                  </div>
                  
                  <div className="mt-2">
                    <input
                      type="file"
                      id="institutionProof"
                      accept=".jpg,.jpeg,.png,.gif,.webp,.pdf"
                      onChange={(e) => handleFileChange(e, 'institutionProof')}
                      className="hidden"
                    />
                    
                    {!files.institutionProof ? (
                      <label
                        htmlFor="institutionProof"
                        className="flex flex-col items-center justify-center w-full p-5 border-2 border-dashed border-slate-200 rounded-xl cursor-pointer bg-white hover:bg-slate-50/80 hover:border-purple-400 transition-all"
                      >
                        <Upload className="h-5 w-5 text-slate-400 mb-1" />
                        <span className="text-xs font-semibold text-slate-600">Select incorporation file</span>
                        <span className="text-[10px] text-slate-400 mt-0.5">Images or PDF up to 5MB</span>
                      </label>
                    ) : (
                      <div className="flex items-center justify-between p-3 bg-white border border-slate-200 rounded-lg shadow-3xs">
                        <div className="flex items-center min-w-0 gap-2">
                          {getFileIcon(files.institutionProof)}
                          <span className="text-xs font-semibold text-slate-700 truncate">
                            {files.institutionProof.name}
                          </span>
                          <span className="text-[10px] text-slate-400 flex-shrink-0">
                            ({(files.institutionProof.size / 1024 / 1024).toFixed(2)} MB)
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeFile('institutionProof')}
                          className="p-1 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded transition-colors"
                        >
                          <XCircle className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                    
                    {errors.institutionProof && (
                      <p className="mt-1 text-xs text-rose-600 font-medium flex items-center gap-1">
                        <AlertCircle className="w-3.5 h-3.5" />
                        {errors.institutionProof}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Action Operations submission section */}
              <div className="pt-4 border-t border-slate-100">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full flex justify-center py-2.5 px-4 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold shadow-sm transition-colors disabled:opacity-60"
                >
                  {isSubmitting ? (
                    <span className="flex items-center gap-2">
                      <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Uploading Application Profile...
                    </span>
                  ) : (
                    'Submit Application Profile'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Footer Support contextual baseline links */}
        <div className="mt-8 text-center text-xs text-slate-400">
          <p>Encountering issues? Send an inquiry directly to <a href="mailto:support@truyon.com" className="text-blue-600 hover:underline">support@truyon.com</a></p>
        </div>
      </div>
    </div>
  );
};

export default ApplicationForm;